import { FC, Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Menu, MenuItem, Popover, Stack, Typography } from '@mui/material';
import { Tooltip } from '@mui/material';

import { Button } from '@components/base';
import { SortOrder } from '@models/OrderBy';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { getSortableFieldDefs } from '@modules/layouts/hooks/useSearchAndFilter/utils';

import OrderByPopupContent from './OrderByPopoverContent';
import { getNewEntry } from './utils';

const OrderByPopup: FC = () => {
  const { t } = useTranslation();

  const {
    filterDefLoad: { error, isLoading: filterDefsLoading, data: filterDefs },
  } = useSearchAndFilter();

  const {
    isReadonly,
    searchQuery: { orderBy },
    setOrderBy,
  } = useSearchAndFilter();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const sortableFieldDefs = getSortableFieldDefs(filterDefs);

  const getDisplayName = (propertyName: string) =>
    sortableFieldDefs.find(def => def.propertyName === propertyName)?.displayName;

  const getSortOrderPartial = (sortOrder: SortOrder) =>
    `(${t('sort.orderShort', {
      context: sortOrder,
    })})`;

  return (
    <Fragment>
      <Tooltip
        title={
          orderBy.length > 0 ? (
            orderBy.length === 1 ? (
              `${getDisplayName(orderBy[0].field)} ${getSortOrderPartial(orderBy[0].sortOrder)}`
            ) : (
              <Box display='grid' gridTemplateColumns='auto auto auto'>
                {orderBy.map(({ field, id, sortOrder }, index) => (
                  <Fragment key={id}>
                    <Typography component='span' mr={1} variant='subtitle2'>
                      {index + 1}.
                    </Typography>

                    <Typography component='span' mr={1} variant='subtitle2'>
                      {getDisplayName(field)}
                    </Typography>

                    <Typography component='span' variant='subtitle2'>
                      {getSortOrderPartial(sortOrder)}
                    </Typography>
                  </Fragment>
                ))}
              </Box>
            )
          ) : undefined
        }
      >
        <Stack alignItems='center' direction='row' spacing={1}>
          {orderBy.length > 0 && (
            <Typography variant='caption' whiteSpace='nowrap'>
              {t('sort.orderBy')}
            </Typography>
          )}

          <Button
            color='inherit'
            disabled={isReadonly}
            onClick={handleOpen}
            rightIcon='chevronDown'
          >
            {orderBy.length > 0
              ? `${getDisplayName(orderBy[0].field)}${
                  orderBy.length > 1 ? ` (+${orderBy.length - 1})` : ''
                }`
              : t('sort.orderBy')}
          </Button>
        </Stack>
      </Tooltip>

      {orderBy.length > 0 || filterDefsLoading || error ? (
        <Popover
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          onClose={handleClose}
          open={open}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        >
          <OrderByPopupContent orderBy={orderBy} setOrderBy={setOrderBy} />
        </Popover>
      ) : (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          onClose={handleClose}
          open={open}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItem onClick={handleClose} selected>
            {t('default')}
          </MenuItem>

          {sortableFieldDefs &&
            sortableFieldDefs.length > 0 &&
            sortableFieldDefs.map(option => (
              <MenuItem
                key={option.propertyName}
                onClick={() => setOrderBy([getNewEntry(option.propertyName)])}
              >
                {option.displayName}
              </MenuItem>
            ))}
        </Menu>
      )}
    </Fragment>
  );
};

export default OrderByPopup;
